import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useCallback, useContext, useMemo, useSyncExternalStore } from "react";
import { useCookies } from "react-cookie";
import { cookieSetOptions } from "../cookieSetOptions";
import { UNIFIED_USER_INFO_COOKIE_NAME } from "./constants";
import { UnifiedAuthContext, UnifiedAuthContextInterface } from "./context";
import { getUnifiedAccounts } from "./fetch";
import {
  getSessionUsername,
  removeSessionUsername,
  setSessionUsername,
  subscribeToSessionUsername,
} from "./sessionUsername";

/**
 * Use the `useUnifiedAuth` hook in your components to access the auth state and methods.
 */
export const useUnifiedAuth = (context = UnifiedAuthContext): UnifiedAuthContextInterface =>
  useContext(context) as UnifiedAuthContextInterface;

export interface UnifiedUserInfo {
  given_name: string;
  family_name: string;
  email: string;
}

export interface UnifiedUserInfoCookieInterface {
  setUnifiedUserInfo: (info: UnifiedUserInfo) => void;
  removeUnifiedUserInfo: () => void;
}

export const useUnifiedUserInfoCookie = (): UnifiedUserInfoCookieInterface => {
  const [, setCookie, removeCookie] = useCookies([UNIFIED_USER_INFO_COOKIE_NAME]);
  const setUnifiedUserInfo = useCallback(
    (info: UnifiedUserInfo) =>
      setCookie(UNIFIED_USER_INFO_COOKIE_NAME, info, {
        sameSite: "strict",
      }),
    [setCookie]
  );

  const removeUnifiedUserInfo = useCallback(
    () => removeCookie(UNIFIED_USER_INFO_COOKIE_NAME, cookieSetOptions()),
    [removeCookie]
  );

  return useMemo(
    () => ({
      setUnifiedUserInfo,
      removeUnifiedUserInfo,
    }),
    [removeUnifiedUserInfo, setUnifiedUserInfo]
  );
};

/**
 * The session username as `[value, set, remove]`. Every caller sees the same value: it lives in
 * sessionStorage and is shared through ./sessionUsername, not held in per-component state.
 */
export function useSessionUsername(): [string, (value: string) => void, () => void] {
  const value = useSyncExternalStore(subscribeToSessionUsername, getSessionUsername);
  return useMemo(() => [value, setSessionUsername, removeSessionUsername], [value]);
}

const getUnifiedAccountsQuery = (
  access_token: string,
  { enabled = true }: Partial<UseQueryOptions> = {}
) => ({
  queryKey: ["getUnifiedAccounts", access_token],
  queryFn: async () => {
    const unifiedAccounts = await getUnifiedAccounts(access_token);
    return unifiedAccounts;
  },
  enabled,
});

export const useUnifiedAccountsQuery = (access_token: string) =>
  useQuery(getUnifiedAccountsQuery(access_token, { enabled: Boolean(access_token) }));
